import { Image } from '@leagueplatform/genesis-commons';
import {
  Button,
  StackLayout,
  Box,
  HeadingText,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { useIntl } from '@leagueplatform/locales';
import { track } from '@leagueplatform/analytics';
import * as React from 'react';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { DASHBOARD_ASSET_MAP } from '#types/dashboard-assets.type.ts';
import { UnavailableDeviceConnectModal } from '@leagueplatform/health-programs';
import { useIsSelfReportedDataEnabled } from '#hooks/use-is-self-reported-data-enabled.hook.ts';
import { AddMetricDataButton } from './add-metric-data-button';

type NoDataProps = {
  metric: string;
  canSelfRecordData: boolean;
  canConnectDevice: boolean;
};

export const NoDataAvailable = ({
  metric,
  canSelfRecordData,
  canConnectDevice,
}: NoDataProps) => {
  const { formatMessage } = useIntl();
  const { isSelfReportedDataEnabled } = useIsSelfReportedDataEnabled();
  const { dataType } = useParams<{ dataType: string }>();
  const connectedDeviceImage = handleStaticAsset(
    DASHBOARD_ASSET_MAP.DASHBOARD_NO_HEALTH_METRIC_DATA,
  ) as string;

  const [displayUnavailableOnWebModal, setDisplayUnavailableOnWebModal] =
    React.useState(false);

  const showAddDataButton = canSelfRecordData && isSelfReportedDataEnabled;

  return (
    <Box
      css={{
        marginTop: '$twoAndHalf',
        marginBottom: '$five',
      }}
    >
      <StackLayout
        horizontalAlignment="center"
        verticalAlignment="center"
        spacing="$one"
        css={{
          paddingTop: '$two',
          paddingBottom: '$two',
          backgroundColor: '$surfaceBackgroundTertiary',
          borderColor: '$onSurfaceBorderSubdued',
          borderWidth: '$thin',
          borderRadius: '$small',
          borderStyle: 'solid',
        }}
      >
        <Box>
          <Image width={215} src={connectedDeviceImage} alt="altTxt" />
        </Box>
        <StackLayout verticalAlignment="center" horizontalAlignment="center">
          <HeadingText
            size="md"
            level="2"
            css={{ typography: '$subtitleOne', marginBottom: '$quarter' }}
          >
            {formatMessage({
              id: 'NO_DATA_AVAILABLE',
            })}
          </HeadingText>

          <UtilityText as="p" css={{ marginBottom: '$oneAndHalf' }}>
            {formatMessage(
              { id: 'CURRENTLY_NOT_SHARING_DATA' },
              {
                metric,
              },
            )}
          </UtilityText>
          <StackLayout css={{ maxWidth: 295, width: '100%' }} spacing="$half">
            {showAddDataButton ? <AddMetricDataButton /> : null}
            {canConnectDevice ? (
              <Button
                width="fillContainer"
                priority="secondary"
                onClick={() => {
                  setDisplayUnavailableOnWebModal(true);
                  track(
                    'dashboard.button_clicked.metric_item_metric_type.add_data',
                    {
                      metric_type: dataType,
                      content_id: '',
                      contentful_system: '',
                      metric_id: '',
                      is_empty_state: 'true',
                    },
                  );
                }}
              >
                {formatMessage({ id: 'CONNECT_A_DEVICE' })}
              </Button>
            ) : null}
          </StackLayout>
        </StackLayout>
      </StackLayout>
      {displayUnavailableOnWebModal ? (
        <UnavailableDeviceConnectModal
          onClose={() => setDisplayUnavailableOnWebModal(false)}
        />
      ) : null}
    </Box>
  );
};
